import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Lock, Printer } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Field, SelectField, inputCls } from "./Field";
import { FileDrop } from "./FileDrop";
import { useHumanCheck } from "./HumanCheck";
import { api, errMsg } from "../../lib/api";
import { DISTRICTS, EMPLOYMENT_CATEGORIES, EXPERIENCE, GENDERS, MEMBERSHIP_CATEGORIES } from "../../data/content";

const EMPTY = {
  full_name: "", dob: "", gender: "", mobile: "", whatsapp: "", email: "", address: "", district: "",
  employer: "", designation: "", employment_category: "", experience_years: "", membership_category: "",
};
const SELECTS = ["gender", "district", "employment_category", "experience_years", "membership_category"];

const Success = ({ result, onReset }) => (
  <div className="rounded-2xl border border-leaf/30 bg-white p-8 text-center shadow-sm sm:p-12" data-testid="application-success">
    <CheckCircle2 className="mx-auto h-14 w-14 text-leaf" />
    <h3 className="mt-5 font-display text-2xl font-extrabold text-navy">Application submitted successfully</h3>
    <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">Thank you, {result.name}. Your membership application has been received and will be reviewed by the federation office.</p>
    <div className="mx-auto mt-6 inline-block rounded-xl border border-dashed border-royal/40 bg-royal-light/40 px-8 py-4">
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Application reference number</p>
      <p className="mt-1 font-mono text-2xl font-bold text-royal" data-testid="application-ref-id">{result.ref_id}</p>
    </div>
    <p className="mt-4 text-xs text-slate-500">Use this number with your registered mobile number to track your application.</p>
    <div className="no-print mt-8 flex flex-col justify-center gap-3 sm:flex-row">
      <button onClick={() => window.print()} className="btn-outline" data-testid="application-print-ack"><Printer className="h-4 w-4" /> Print acknowledgement</button>
      <Link to="/membership?tab=track" onClick={onReset} className="btn-primary" data-testid="application-track-link">Track application</Link>
    </div>
  </div>
);

export const ApplicationForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [idProof, setIdProof] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const human = useHumanCheck("application");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    const missing = SELECTS.find((k) => !form[k]);
    if (missing) return toast.error(`Please select ${missing.replace(/_/g, " ")}.`);
    if (!photo || !idProof) return toast.error("Please upload your photograph and ID proof.");
    if (!consent) return toast.error("Please accept the declaration and consent.");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("consent", "true");
    fd.append("photo", photo);
    fd.append("id_proof", idProof);
    Object.entries(human.payload).forEach(([k, v]) => fd.append(k, v));
    setBusy(true);
    try {
      const { data } = await api.post("/applications", fd);
      setResult({ ...data, name: form.full_name });
      setForm(EMPTY); setPhoto(null); setIdProof(null); setConsent(false);
      window.scrollTo({ top: 300, behavior: "smooth" });
      human.refresh();
    } catch (err) {
      toast.error(errMsg(err));
      human.handleError(err);
    } finally {
      setBusy(false);
    }
  };

  if (result) return <Success result={result} onReset={() => setResult(null)} />;

  const T = (k) => `application-${k.replace(/_/g, "-")}`;
  return (
    <form onSubmit={submit} className="space-y-10 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10" data-testid="application-form">
      <fieldset className="grid gap-5 sm:grid-cols-2">
        <legend className="mb-5 font-display text-lg font-bold text-navy">1. Personal details</legend>
        <Field label="Full Name" required><Input className={inputCls} required value={form.full_name} onChange={set("full_name")} data-testid={T("full_name")} /></Field>
        <Field label="Date of Birth" required><Input className={inputCls} type="date" required max={new Date().toISOString().slice(0, 10)} value={form.dob} onChange={set("dob")} data-testid={T("dob")} /></Field>
        <Field label="Gender" required><SelectField value={form.gender} onChange={set("gender")} options={GENDERS} testId={T("gender")} /></Field>
        <Field label="Email Address" required><Input className={inputCls} type="email" required value={form.email} onChange={set("email")} data-testid={T("email")} /></Field>
        <Field label="Mobile Number" required hint="10-digit Indian mobile number"><Input className={inputCls} required inputMode="numeric" maxLength={13} value={form.mobile} onChange={set("mobile")} data-testid={T("mobile")} /></Field>
        <Field label="WhatsApp Number" hint="Leave blank if same as mobile"><Input className={inputCls} inputMode="numeric" maxLength={13} value={form.whatsapp} onChange={set("whatsapp")} data-testid={T("whatsapp")} /></Field>
        <Field label="Residential Address" required className="sm:col-span-2"><Textarea rows={3} required value={form.address} onChange={set("address")} className="bg-white" data-testid={T("address")} /></Field>
        <Field label="District" required><SelectField value={form.district} onChange={set("district")} options={DISTRICTS} testId={T("district")} /></Field>
      </fieldset>
      <fieldset className="grid gap-5 sm:grid-cols-2">
        <legend className="mb-5 font-display text-lg font-bold text-navy">2. Employment details</legend>
        <Field label="Company / Employer Name" required><Input className={inputCls} required value={form.employer} onChange={set("employer")} data-testid={T("employer")} /></Field>
        <Field label="Designation" required><Input className={inputCls} required value={form.designation} onChange={set("designation")} data-testid={T("designation")} /></Field>
        <Field label="Employment Category" required><SelectField value={form.employment_category} onChange={set("employment_category")} options={EMPLOYMENT_CATEGORIES} testId={T("employment_category")} /></Field>
        <Field label="Years of Experience" required><SelectField value={form.experience_years} onChange={set("experience_years")} options={EXPERIENCE} testId={T("experience_years")} /></Field>
        <Field label="Membership Category" required className="sm:col-span-2"><SelectField value={form.membership_category} onChange={set("membership_category")} options={MEMBERSHIP_CATEGORIES} testId={T("membership_category")} /></Field>
      </fieldset>
      <fieldset className="grid gap-5 sm:grid-cols-2">
        <legend className="mb-5 font-display text-lg font-bold text-navy">3. Documents</legend>
        <FileDrop label="Photograph" accept="image/*" file={photo} onChange={setPhoto} testId="application-photo" hint="Passport-size photo. Large phone photos are resized automatically" />
        <FileDrop label="ID Proof" accept="image/*,application/pdf" file={idProof} onChange={setIdProof} testId="application-id-proof" hint="Aadhaar, company ID or other govt. ID. Photo or PDF (PDF max 5 MB)" />
      </fieldset>
      <div className="rounded-xl bg-slate-50 p-5">
        <label className="flex items-start gap-3 text-sm text-slate-700">
          <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="application-consent" />
          <span><strong>Declaration and consent:</strong> I declare that the information provided is true and correct to the best of my knowledge. I agree to abide by the rules and bye-laws of the IT Employees Federation, and I consent to the federation storing and using my personal information for membership purposes as described in the <Link to="/privacy-policy" className="font-semibold text-royal underline" target="_blank">Privacy Policy</Link>.</span>
        </label>
      </div>
      {human.ui}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-2 text-xs text-slate-500"><Lock className="h-3.5 w-3.5" /> Your documents are stored privately and are visible only to authorised federation staff.</p>
        <button type="submit" disabled={busy} className="btn-green !py-3.5 disabled:opacity-60" data-testid="application-submit-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} SUBMIT MEMBERSHIP APPLICATION
        </button>
      </div>
    </form>
  );
};
